import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  createLabelModel,
  deleteLabelModel,
  downloadBlob,
  getLabelMacros,
  getLabelModels,
  updateLabelModel,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  lerLayoutSalvo,
  lerZetiq,
  modeloPadrao,
  montarArquivoZetiq,
  nomeArquivoZetiq,
} from '../../utils/etiqueta/modelo';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import EditorModeloEtiqueta from './etiqueta/EditorModeloEtiqueta';

const EMPTY = {
  descricao: '',
  qtdColunas: 1,
  template: `TamanhoDescricao=20
I8,A,001
q609
Q203,24
D11
ZT
^XA
^FO30,20^A0N,28,28^FD{produto1.descricao-1}^FS
^FO30,50^A0N,36,36^FD{produto1.precovarejo}^FS
^FO30,95^BY2^BCN,50,Y,N,N^FD{produto1.ean}^FS
^XZ`,
};

export default function LabelModelsPage() {
  const [items, setItems] = useState([]);
  const [macros, setMacros] = useState([]);
  const [macroPrefixo, setMacroPrefixo] = useState('produto1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [info, setInfo] = useState('');
  const [editor, setEditor] = useState(null);
  const [macrosVisiveis, setMacrosVisiveis] = useState(false);
  const arquivoRef = useRef(null);

  const exportarItem = (item) => {
    if (item.layoutJson) {
      const layout = lerLayoutSalvo(item.layoutJson);
      if (!layout.ok) {
        setError(layout.error);
        return;
      }
      const arquivo = montarArquivoZetiq({ descricao: item.descricao, layout: layout.layout });
      downloadBlob(
        new Blob([JSON.stringify(arquivo, null, 2)], { type: 'application/json' }),
        nomeArquivoZetiq(item.descricao),
      );
      return;
    }
    const arquivo = montarArquivoZetiq({
      descricao: item.descricao,
      template: item.template,
      qtdColunas: item.qtdColunas,
      layout: null,
    });
    downloadBlob(
      new Blob([JSON.stringify(arquivo, null, 2)], { type: 'application/json' }),
      nomeArquivoZetiq(item.descricao),
    );
  };

  const abrirGrafico = (item) => {
    if (!item) {
      setEditor({ id: null, descricao: '', layout: modeloPadrao(), aviso: '', erro: '' });
      return;
    }
    const layout = lerLayoutSalvo(item.layoutJson);
    if (!layout.ok) {
      setError(layout.error);
      return;
    }
    setEditor({ id: item.id, descricao: item.descricao, layout: layout.layout, aviso: '', erro: '' });
  };

  const importarArquivo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setInfo('');
    try {
      const texto = await file.text();
      const lido = lerZetiq(texto);
      if (!lido.ok) {
        setError(lido.error);
        return;
      }
      if (lido.layout) {
        setEditor({
          id: null,
          descricao: lido.descricao,
          layout: lido.layout,
          aviso: `Arquivo ${file.name} carregado. Revise os passos e salve para gravar no sistema.`,
          erro: '',
        });
        return;
      }
      setEditingId(null);
      setForm({ descricao: lido.descricao, qtdColunas: lido.qtdColunas, template: lido.template });
      setOpen(true);
      setInfo(`Código de ${file.name} carregado. Salve para gravar o modelo de texto.`);
    } catch {
      setError('Não foi possível ler o arquivo .zetiq.');
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getLabelModels();
      setItems(response.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível carregar os modelos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const { data } = await getLabelMacros();
        setMacros(data?.macros || []);
        if (data?.prefixo) setMacroPrefixo(data.prefixo);
      } catch {
        /* macros opcionais */
      }
    })();
  }, []);

  const save = async () => {
    if (!form.descricao.trim()) {
      setError('Informe a descrição.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        descricao: form.descricao.trim(),
        qtdColunas: Number(form.qtdColunas) || 1,
        template: form.template || '',
        layoutJson: null,
        atualizarLayout: true,
      };
      if (editingId) await updateLabelModel(editingId, payload);
      else await createLabelModel(payload);
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível salvar o modelo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Modelos de etiqueta"
        subtitle="Desenhe a etiqueta no assistente ou cole o código da impressora. Arquivos .zetiq levam o modelo para outro sistema."
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={() => abrirGrafico(null)}>
              Novo desenho
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setEditingId(null);
                setForm(EMPTY);
                setOpen(true);
              }}
            >
              Novo código
            </Button>
            <Button startIcon={<FileUploadIcon />} variant="outlined" onClick={() => arquivoRef.current?.click()}>
              Importar .zetiq
            </Button>
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      <input ref={arquivoRef} type="file" accept=".zetiq,.json,application/json" hidden onChange={importarArquivo} />
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Macros de produto
            </Typography>
            {!macrosVisiveis && (
              <Typography variant="body2" color="text.secondary">
                Referência de campos {'{produtoN.…}'} para colar no código ou no desenho.
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            endIcon={macrosVisiveis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setMacrosVisiveis((v) => !v)}
          >
            {macrosVisiveis ? 'Ocultar macros' : 'Exibir macros'}
          </Button>
        </Stack>
        {macrosVisiveis && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Substitua <code>N</code> pelo número da coluna (1, 2, 3… conforme o modelo). Clique no chip para inserir no template de código.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
              {macros.map((m) => (
                <Chip
                  key={m.chave}
                  size="small"
                  variant="outlined"
                  label={`{${macroPrefixo}.${m.chave}}`}
                  title={`${m.descricao}${m.exemplo ? ` — ex.: ${m.exemplo}` : ''}`}
                  onClick={() => {
                    const token = `{${macroPrefixo}.${m.chave}}`;
                    setForm((p) => ({ ...p, template: `${p.template || ''}${token}` }));
                    setOpen(true);
                  }}
                />
              ))}
            </Stack>
            <ResponsiveTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Macro</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Exemplo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {macros.map((m) => (
                    <TableRow key={m.chave}>
                      <TableCell data-label="Macro">
                        <code>{`{${macroPrefixo}.${m.chave}}`}</code>
                      </TableCell>
                      <TableCell data-label="Descrição">{m.descricao}</TableCell>
                      <TableCell data-label="Exemplo">{m.exemplo || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTable>
          </Box>
        )}
      </Paper>

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell align="right">Colunas</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell data-label="Descrição">{item.descricao}</TableCell>
                <TableCell data-label="Tipo">
                  <Chip size="small" label={item.layoutJson ? 'Gráfico' : 'Código'} variant="outlined" />
                </TableCell>
                <TableCell align="right" data-label="Colunas">{item.qtdColunas}</TableCell>
                <TableCell align="right" data-actions="">
                  <IconButton size="small" title="Exportar .zetiq" onClick={() => exportarItem(item)}>
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title={item.layoutJson ? 'Editar desenho' : 'Editar código'}
                    onClick={() => {
                      if (item.layoutJson) {
                        abrirGrafico(item);
                        return;
                      }
                      setEditingId(item.id);
                      setForm({
                        descricao: item.descricao,
                        qtdColunas: item.qtdColunas,
                        template: item.template,
                      });
                      setOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={async () => {
                      if (!window.confirm(`Excluir o modelo "${item.descricao}"?`)) return;
                      try {
                        await deleteLabelModel(item.id);
                        await load();
                      } catch (e) {
                        setError(getApiErrorMessage(e, 'Não foi possível excluir o modelo.'));
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Editar modelo' : 'Novo modelo'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Descrição"
              value={form.descricao}
              onChange={(e) => setForm((p) => ({ ...p, descricao: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Colunas"
              type="number"
              value={form.qtdColunas}
              onChange={(e) => setForm((p) => ({ ...p, qtdColunas: e.target.value }))}
              inputProps={{ min: 1, max: 6 }}
            />
            <TextField
              label="Template"
              value={form.template}
              onChange={(e) => setForm((p) => ({ ...p, template: e.target.value }))}
              fullWidth
              multiline
              minRows={12}
              helperText="PPLA/ZPL/EPL. Clique nas macros acima para inserir no template."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={loading}>Salvar</Button>
        </DialogActions>
      </Dialog>
      {editor && (
        <EditorModeloEtiqueta
          key={editor.id ?? 'novo'}
          modeloId={editor.id}
          descricaoInicial={editor.descricao}
          layoutInicial={editor.layout}
          aviso={editor.aviso}
          erro={editor.erro}
          saving={loading}
          onClose={() => setEditor(null)}
          onSave={async (payload) => {
            setLoading(true);
            setEditor((atual) => (atual ? { ...atual, erro: '' } : atual));
            try {
              const { id, ...corpo } = payload;
              if (id) await updateLabelModel(id, corpo);
              else await createLabelModel(corpo);
              setEditor(null);
              setInfo('Modelo gráfico salvo.');
              await load();
            } catch (e) {
              const msg = getApiErrorMessage(e, 'Não foi possível salvar o modelo.');
              setEditor((atual) => (atual ? { ...atual, erro: msg } : atual));
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
    </Box>
  );
}
